import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Spinner,
  Show,
  Divider,
  Center,
  Text,
  Button,
  Tooltip,
} from "@chakra-ui/react";
import { EventParams } from "firebase/analytics";

import { TextPrimary, TextSecondary } from "theme";
import { useStakeProvider } from "dapp-sdk";
import { iconMapping } from "icons";
import { AiOutlineInfoCircle } from "react-icons/ai";

import { DefiData, StrapiDefiPageData } from "@customtypes/global";
import usePageData from "@hooks/usePageData";

import { getDefiData } from "services/defi";

import DefiTable from "@components/Defi/DefiTable";
import Item from "@components/Defi";
import Icon from "@components/Icon";

import { trackEvent } from "@utils/firebase";
import { sentryErrorLog } from "@utils/sentryLog";
import { currencyToNumber } from "@utils/common";

import {
  ALL_FILTER,
  CATEGORY_FILTER,
  DEFI_CAROUSEL_CTA,
  TAG_FILTER,
} from "@constants/analytics";
import { BNB } from "@constants/constants";

import _keys from "lodash/keys";
import _intersection from "lodash/intersection";

import styles from "./styles.module.scss";
import Disclaimer from "@components/Disclaimer";

enum FilterType {
  ALL = "ALL",
  TAG = "TAG",
  CATEGORY = "CATEGORY",
}

type Filter = {
  [key: string]: boolean;
};

const initialFilterValue = {
  allFilter: true,
  categoryFilters: {} as Filter,
  tagFilters: {} as Filter,
};

const DeFiPage = () => {
  const defiStrapiPageData = usePageData("$.page.defiData")[0];
  const [data, setData] = useState<DefiData>({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(initialFilterValue);

  const { firebaseEventsData } = useStakeProvider();

  useEffect(() => {
    (async function getData() {
      try {
        const response = await getDefiData();
        setData(response);
      } catch (e) {
        sentryErrorLog(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!filters.allFilter) {
      const isTagFilterActive = _keys(filters.tagFilters).some(
        (key) => filters.tagFilters[key]
      );

      const isCategoryFilterActive = _keys(filters.categoryFilters).some(
        (key) => filters.categoryFilters[key]
      );

      if (!(isTagFilterActive || isCategoryFilterActive)) {
        setFilters((prev) => ({ ...prev, allFilter: true }));
      }
    }
  }, [filters]);

  const logFirebaseEvent = useCallback(
    (event: string, eventParams: EventParams = {}) => {
      trackEvent(event, {
        ...eventParams,
        ...firebaseEventsData,
      });
    },
    [firebaseEventsData]
  );

  const getDefiItemsData = (data: any) => {
    return data?.map((defiItem: any) => {
      return {
        title: defiItem?.title,
        hasApy: defiItem?.has_apy,
        isSDPools: defiItem?.is_sd_pools,
        items: defiItem?.items.map((item: any) => {
          return {
            apiKey: item?.api_key,
            apyMessage: item?.apy_message,
            showToken: item?.show_token,
            token: item?.token,
            platform: item?.platform_detail?.data?.attributes || null,
            pairPlatform: item?.pair_platform_detail?.data?.attributes || null,
            type: item?.type?.data?.attributes?.key || "",
            tokenIconKey: item?.token_icon_key,
            links:
              item?.action_links?.data?.attributes?.links.map((link: any) => {
                return {
                  actionCta: link?.action_cta,
                  actionLink: link?.action_link,
                  buttonType: link?.button_type,
                };
              }) || [],
          };
        }),
      };
    });
  };

  const defiPageData: StrapiDefiPageData = useMemo(() => {
    return {
      ...defiStrapiPageData,
      banner: defiStrapiPageData?.banner,
      defiItems: getDefiItemsData(defiStrapiPageData?.defi_items),
      title: defiStrapiPageData?.page_title,
      subTitle: defiStrapiPageData?.page_subtitle,
      tooltipText: defiStrapiPageData?.tooltip_text,
    };
  }, [defiStrapiPageData]);

  const availableFilters = useCallback(() => {
    const filters = [{ label: "All", type: FilterType.ALL }];

    const alreadyAddedTags: string[] = [];

    defiPageData.defiItems.forEach(({ items }) => {
      items.forEach(({ apiKey }) => {
        const tags = data[apiKey]?.tags || [];
        tags.forEach((tag) => {
          if (alreadyAddedTags.indexOf(tag.toLowerCase()) === -1) {
            alreadyAddedTags.push(tag.toLowerCase());
            filters.push({
              type: FilterType.TAG,
              label: tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase(),
            });
          }
        });
      });
    });

    defiPageData.defiItems.forEach(({ title }) => {
      filters.push({ type: FilterType.CATEGORY, label: title });
    });

    return filters;
  }, [data, defiPageData]);

  const isFilterSelected = useCallback(
    (filter: { label: string; type: string }) => {
      switch (filter.type) {
        case FilterType.ALL:
          return filters.allFilter;

        case FilterType.TAG:
          return !!filters.tagFilters[filter.label];

        case FilterType.CATEGORY:
          return filters.categoryFilters[filter.label];
      }
    },
    [filters]
  );

  const handleFiltersChange = useCallback(
    (filter: { label: string; type: string }) => {
      if (filter.type === FilterType.ALL) {
        logFirebaseEvent(ALL_FILTER);
        setFilters(initialFilterValue);
        return;
      }
      const isTagFilter = filter.type === FilterType.TAG;
      const key = isTagFilter ? "tagFilters" : "categoryFilters";
      const event = isTagFilter ? TAG_FILTER : CATEGORY_FILTER;

      logFirebaseEvent(event, { label: filter.label });

      if (filter.label in filters[key]) {
        const newFilters = {
          ...filters[key],
          [filter.label]: !filters[key][filter.label],
        };

        setFilters((prev) => ({
          ...prev,
          [key]: newFilters,
          allFilter: false,
        }));
      } else {
        const newFilters = { ...filters[key], [filter.label]: true };

        setFilters((prev) => ({
          ...prev,
          [key]: newFilters,
          allFilter: false,
        }));
      }
    },
    [filters]
  );

  const getFilteredItems = useCallback(() => {
    if (filters.allFilter) {
      return defiPageData.defiItems;
    }

    const activeTagFilters = _keys(filters.tagFilters).filter(
      (key) => filters.tagFilters[key]
    );

    return defiPageData.defiItems
      .map((category) => {
        if (filters.categoryFilters[category.title]) {
          return category;
        }

        return {
          ...category,
          items: (category.items as any[]).filter(
            (item: any) =>
              _intersection(
                activeTagFilters.map((key) => key.toUpperCase()),
                data[item.apiKey]
                  ? (data[item.apiKey].tags || []).map((key) =>
                      key.toUpperCase()
                    )
                  : []
              ).length
          ),
        };
      })
      .filter((category) => {
        return category.items.length > 0;
      });
  }, [data, filters, defiPageData]);

  const { banner, tooltipText } = defiPageData;

  return (
    <>
      <Box
        ml={{ base: 0, xl: 60 }}
        px={{ base: "1rem", xl: "2.5rem" }}
        mt={{ base: "1rem", lg: "1rem" }}
        height={{ xl: "calc(100vh - 128px)" }}
        overflow={{ xl: "auto" }}
        className={styles.hideScroll}
      >
        {banner && banner?.title ? (
          <Show above="lg">
            <Center mt="1rem">
              <Flex
                align="center"
                w="872px"
                h="160px"
                borderRadius="24px"
                gap="1rem"
                bg="linear-gradient(90deg, rgba(55,41,0) 20%, rgba(30,23,3) 50%, rgba(55,41,0) 90%);"
              >
                <Flex
                  gap={{ base: "0.5rem", xl: "1rem" }}
                  flex={1}
                  flexDir="column"
                  px="92px"
                  align="flex-start"
                >
                  <Text
                    fontSize={24}
                    fontWeight="500"
                    lineHeight="28px"
                    color="white"
                  >
                    {banner?.title}
                  </Text>
                  {banner?.cta_text && (
                    <Button
                      as="a"
                      href={banner?.cta_link}
                      target="_blank"
                      rel="noreferrer"
                      fontWeight="500"
                      fontSize={16}
                      bg="primary"
                      onClick={() => {
                        logFirebaseEvent(DEFI_CAROUSEL_CTA);
                      }}
                    >
                      {banner.cta_text}
                    </Button>
                  )}
                </Flex>
                <Box mr="72px">
                  {banner.img_key && (
                    <Icon
                      height="160px"
                      width="160px"
                      Icon={iconMapping(banner.img_key)}
                    />
                  )}
                </Box>
              </Flex>
            </Center>
          </Show>
        ) : null}
        <Flex
          flexDir="column"
          gap="0.5rem"
          align={{ base: "flex-start", lg: "center" }}
          mt={banner ? "2.5rem" : 0}
        >
          <Flex align="center" gap="1.5rem">
            <TextPrimary
              fontSize={{ base: "20px", lg: "28px" }}
              lineHeight={{ base: "24px", lg: "30px" }}
              fontWeight="600"
            >
              {defiPageData?.title}
            </TextPrimary>
            {tooltipText && (
              <Tooltip
                label={
                  <Box pointerEvents="all" textAlign="left" maxW="250px">
                    {tooltipText}
                  </Box>
                }
                p="1rem"
                borderRadius="8px"
                closeDelay={2000}
              >
                <Text ml="4px" color="textSecondaru" fontSize="14px">
                  <AiOutlineInfoCircle />
                </Text>
              </Tooltip>
            )}
          </Flex>

          {defiPageData?.subTitle && (
            <TextSecondary
              fontSize={{ base: "12px", lg: "16px" }}
              textAlign={{ base: "left", lg: "center" }}
            >
              {defiPageData?.subTitle}
            </TextSecondary>
          )}

          <Flex
            className="scroll-container"
            w={{ base: "calc(100vw - 1.5rem)", lg: "85%" }}
            justify={{ base: "flex-start", lg: "center" }}
            flexWrap={{ base: "nowrap", lg: "wrap" }}
            gap={{ base: "0.5rem", lg: "1rem" }}
            mt="1.5rem"
            overflowX="auto"
          >
            {availableFilters().map((filter) => (
              <Box
                key={filter.label}
                bg={isFilterSelected(filter) ? "primary" : "transaparent"}
                color={isFilterSelected(filter) ? "white" : "inherit"}
                p={{ base: "4px 8px", lg: "8px 12px" }}
                borderRadius="8px"
                border="1px solid"
                _hover={{
                  bg: isFilterSelected(filter)
                    ? "primary"
                    : "backgroundSecondary",
                }}
                borderColor={isFilterSelected(filter) ? "primary" : "#ACB2B5"}
                cursor="pointer"
                onClick={() => handleFiltersChange(filter)}
              >
                <Text
                  whiteSpace="nowrap"
                  fontSize={{ base: 12, lg: 14 }}
                  lineHeight={{ base: "18px", lg: "20px" }}
                  fontWeight="500"
                >
                  {filter.label}
                </Text>
              </Box>
            ))}
          </Flex>
        </Flex>

        {!loading ? (
          <Flex
            my={{ base: 12, lg: 20 }}
            mb="4 !important"
            gap="8"
            direction="column"
          >
            {getFilteredItems().map((group) => {
              const hasAPY = group.hasApy;
              const isSDPool = group.isSDPools;

              const items = group.items.filter((item) => {
                return process.env.NEXT_PUBLIC_TOKEN === BNB
                  ? data[item.apiKey]?.enable
                  : data[item.apiKey] || false;
              });

              return (
                <Box key={group.title}>
                  <TextPrimary fontWeight="600" fontSize="1rem">
                    {group.title}
                  </TextPrimary>
                  <Show above="lg">
                    <DefiTable
                      key={group.title}
                      group={group.title}
                      isSDPool={!!group.isSDPools}
                      data={items}
                      hasAPY={hasAPY}
                      defiData={data}
                    />
                  </Show>
                  <Show breakpoint="(max-width: 991px)">
                    <Flex
                      gap="1.25rem"
                      mt="4"
                      flexDir="column"
                      bg="backgroundSecondary"
                      p={{ base: "1rem 1.5rem", lg: "2rem 2.5rem" }}
                      borderRadius="1.5rem"
                    >
                      {items
                        .sort((a, b) => {
                          const a_tvl =
                            data[a.apiKey] && data[a.apiKey]?.tvl
                              ? currencyToNumber(data[a.apiKey]?.tvl) || 0
                              : 0;
                          const b_tvl =
                            data[b.apiKey] && data[b.apiKey]?.tvl
                              ? currencyToNumber(data[b.apiKey]?.tvl) || 0
                              : 0;

                          return b_tvl - a_tvl;
                        })
                        .map((item: any, index, items) => (
                          <React.Fragment key={item.id}>
                            <Item
                              TVL={data[item.apiKey]?.tvl}
                              APY={data[item.apiKey]?.apy}
                              hasAPY={hasAPY}
                              isSDPool={isSDPool}
                              paused={data[item.apiKey]?.paused}
                              tags={data[item.apiKey]?.tags || []}
                              itemData={data[item.apiKey]}
                              defiItem={item}
                              links={item.links}
                            />
                            {index !== items.length - 1 && (
                              <Divider borderColor="textSecondary" p="0" />
                            )}
                          </React.Fragment>
                        ))}
                    </Flex>
                  </Show>
                </Box>
              );
            })}
          </Flex>
        ) : (
          <Flex alignItems="center" justifyContent="center" h="500px">
            <Spinner size="lg" color="primary" />
          </Flex>
        )}
        <Box mb={20} textAlign="center">
          <Disclaimer />
        </Box>
      </Box>
    </>
  );
};

export default DeFiPage;
